import { rememberedApplicationsListPath } from '../lib/applicationsListFilters';

/**
 * Path helpers for admin SPA + admin-api when deployed under `/admin` and `/admin/api`.
 */

/** Vite `base` without trailing slash (e.g. `/admin`). */
export const adminAppBasename = import.meta.env.BASE_URL.replace(/\/$/, '');

/** Legacy mount prefix from pre–host-root SPA builds and ingress bookmarks. */
export const legacyAdminPathPrefix = '/admin';

/**
 * Strip a leftover `/admin` prefix when the SPA is served at the host root.
 * Ignored when Vite `base` is already `/admin` (router basename owns the prefix).
 */
export function stripLegacyAdminPrefix(pathname: string): string {
  if (adminAppBasename === legacyAdminPathPrefix) return pathname;
  if (pathname === legacyAdminPathPrefix) return '/';
  if (pathname.startsWith(`${legacyAdminPathPrefix}/`)) {
    return pathname.slice(legacyAdminPathPrefix.length) || '/';
  }
  return pathname;
}

/**
 * Full browser path for `window.location` (includes SPA basename).
 * Do not pass to React Router `navigate` / `<Link to>` — use router paths like `/login` instead.
 */
export function adminRoute(path: string): string {
  const segment = path.startsWith('/') ? path : `/${path}`;
  if (!adminAppBasename) return segment;
  return `${adminAppBasename}${segment}`;
}

/** Strip `/admin` basename segments from a browser or router pathname. */
export function stripAdminBasename(pathname: string): string {
  let path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (adminAppBasename) {
    let changed = true;
    while (changed) {
      changed = false;
      if (path === adminAppBasename) {
        path = '/';
        changed = true;
      } else if (path.startsWith(`${adminAppBasename}/`)) {
        path = path.slice(adminAppBasename.length) || '/';
        changed = true;
      }
    }
  }
  return stripLegacyAdminPrefix(path);
}

/** Whether the browser is already on the login route (avoids redirect loops). */
export function isBrowserOnAdminLoginPage(): boolean {
  const path = stripAdminBasename(window.location.pathname);
  return path === '/login' || path.startsWith('/login/');
}

function defaultPostLoginPath(): string {
  return rememberedApplicationsListPath();
}

const RETIRED_ADMIN_PATH_PREFIXES = [
  '/audit',
  '/admin/audit',
  '/mocking',
  '/service-configs',
  '/dashboard',
];

function isRetiredAdminPath(path: string): boolean {
  if (path === '/') return true;
  return RETIRED_ADMIN_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/** Router-relative `return` query value from the current location. */
export function buildLoginReturnQuery(pathname: string, search = ''): string {
  const path = stripAdminBasename(pathname);
  if (path === '/login' || path.startsWith('/login/')) return defaultPostLoginPath();
  if (isRetiredAdminPath(path)) return defaultPostLoginPath();
  return `${path}${search}`;
}

/**
 * Safe in-app path after login (`return` query param). Avoids `/login` loops and retired tabs.
 */
export function resolvePostLoginPath(returnParam: string | null | undefined): string {
  const raw = (returnParam ?? '').trim();
  if (!raw) return defaultPostLoginPath();

  let path = raw;
  try {
    if (/^https?:\/\//i.test(raw)) {
      path = new URL(raw).pathname;
    }
  } catch {
    return defaultPostLoginPath();
  }

  if (!path.startsWith('/')) path = `/${path}`;
  path = stripAdminBasename(path);

  const firstSegment = path.replace(/^\/+/, '').split('/')[0] ?? '';
  if (firstSegment === 'login') return defaultPostLoginPath();
  if (isRetiredAdminPath(path)) return defaultPostLoginPath();

  return path || defaultPostLoginPath();
}

/**
 * Ensures the base URL ends with `/admin/api` (admin-api mount root, no trailing slash).
 * Upgrades legacy values like `http://localhost:4000` or `.../api` → `.../admin/api`.
 */
export function normalizeAdminApiBase(base: string): string {
  const trimmed = base.trim().replace(/\/$/, '');
  if (!trimmed) return trimmed;
  if (trimmed.endsWith('/admin/api')) return trimmed;
  if (trimmed.endsWith('/api')) {
    return `${trimmed.slice(0, -4)}/admin/api`;
  }
  return `${trimmed}/admin/api`;
}

/**
 * admin-api origin + mount path (no trailing slash).
 * - `VITE_ADMIN_API_BASE` when set (e.g. `http://localhost:4000/admin/api`)
 * - Dev default: `http://localhost:4000/admin/api` (direct to admin-api; no Vite proxy)
 * - Prod default: same-origin `/admin/api`
 */
export function adminApiRoot(): string {
  const raw = import.meta.env.VITE_ADMIN_API_BASE;
  const trimmed = typeof raw === 'string' ? raw.trim().replace(/\/$/, '') : '';
  if (trimmed) {
    return normalizeAdminApiBase(trimmed);
  }
  if (import.meta.env.DEV) {
    return 'http://localhost:4000/admin/api';
  }
  return '/admin/api';
}
